// Settings › Members (Console D, views/settings.js `#/settings/members`; APS-320): the members,
// each with a row menu that changes their role (APS-234) or removes them, or lets you leave
// (APS-212); the open invitations; and inviting by email in a window beside them. The API decides
// who may do what; the page offers only what the viewer may do and says why the rest is not
// theirs. Teams come later (D103 #6).
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { type FormEvent, useId, useRef, useState } from 'react'
import { isApiError } from '../api/client'
import {
  invitationsQuery,
  membersQuery,
  meQuery,
  orgQuery,
  useChangeMemberRole,
  useInvite,
  useRemoveMember,
  useWithdrawInvitation,
} from '../api/queries'
import type { Member, Role } from '../api/types'
import { Btn, EmptyNote, PageHead, Section, Status, Table } from '../components/d'
import { Icon } from '../components/icons'
import { usePopover } from '../components/shell/usePopover'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { day, initials } from '../lib/format'

const ROLE_KEY = { owner: 'role.owner', admin: 'role.admin', member: 'role.member' } as const
const ROLES: Role[] = ['owner', 'admin', 'member']

/**
 * Whether the viewer may remove a member, or leave (APS-212), as the API decides: owners remove
 * anyone, admins anyone but owners, everyone may leave, and the last owner stays.
 */
export function canRemove(member: Member, viewerId: string | undefined, members: Member[]) {
  const viewer = members.find((m) => m.user_id === viewerId)?.role
  const owners = members.filter((m) => m.role === 'owner').length
  if (member.role === 'owner' && owners <= 1) return false
  return (
    member.user_id === viewerId ||
    viewer === 'owner' ||
    (viewer === 'admin' && member.role !== 'owner')
  )
}

/**
 * The roles the viewer may give a member (APS-234), as the API decides: owners give any role,
 * admins change members and admins but never an owner and make no owner, and the last owner
 * stays. Empty when the viewer may not change this member's role at all.
 */
export function roleChoices(member: Member, viewerId: string | undefined, members: Member[]) {
  const viewer = members.find((m) => m.user_id === viewerId)?.role
  const owners = members.filter((m) => m.role === 'owner').length
  if (member.role === 'owner' && owners <= 1) return []
  if (viewer === 'owner') return ROLES
  if (viewer === 'admin' && member.role !== 'owner') return ROLES.filter((r) => r !== 'owner')
  return []
}

type Ask = { kind: 'role'; role: Role } | { kind: 'remove' } | null

/** A member's row menu: the roles the viewer may give, then removing them or leaving. */
function MemberMenu({
  member,
  self,
  choices,
  removable,
  busy,
  onRole,
  onRemove,
}: {
  member: Member
  self: boolean
  choices: Role[]
  removable: boolean
  busy: boolean
  onRole: (role: Role) => void
  onRemove: () => void
}) {
  const { t } = useI18n()
  const pop = usePopover('below')
  const popId = useId()
  const titleId = useId()
  const dialog = useRef<HTMLDialogElement>(null)
  const [ask, setAsk] = useState<Ask>(null)
  const name = member.name || member.email
  const roleName = (r: Role) => t(ROLE_KEY[r])
  const open = (next: Ask) => {
    pop.close()
    setAsk(next)
    dialog.current?.showModal()
  }
  const text =
    ask?.kind === 'role'
      ? {
          title: t('settings.members.roleTitle', { name, role: roleName(ask.role) }),
          body: t(
            ask.role === 'owner' ? 'settings.members.roleOwnerBody' : 'settings.members.roleBody',
          ),
          confirm: t('settings.members.roleConfirm'),
        }
      : ask?.kind === 'remove'
        ? self
          ? {
              title: t('settings.members.leaveTitle'),
              body: t('settings.members.leaveBody'),
              confirm: t('settings.members.leaveConfirm'),
            }
          : {
              title: t('settings.members.removeTitle', { name }),
              body: t('settings.members.removeBody'),
              confirm: t('settings.members.removeConfirm'),
            }
        : null
  return (
    <>
      <button
        ref={pop.anchor}
        type="button"
        className="btn ghost sm"
        aria-expanded={pop.open}
        aria-controls={popId}
        aria-label={t('settings.members.menuFor', { name })}
        disabled={busy}
        onClick={pop.toggle}
      >
        {self && choices.length === 0 ? t('settings.members.leave') : t('settings.members.menu')}
      </button>
      {pop.open ? (
        <section
          ref={pop.pop}
          id={popId}
          className="pop"
          style={pop.style}
          aria-label={t('settings.members.menuFor', { name })}
        >
          {choices.length > 0 ? (
            <>
              <p className="pal-g">{t('settings.members.role')}</p>
              {choices.map((r) => (
                <button
                  key={r}
                  type="button"
                  className="pal-i"
                  aria-pressed={r === member.role}
                  disabled={r === member.role}
                  onClick={() => open({ kind: 'role', role: r })}
                >
                  <span>{t('settings.members.makeRole', { role: roleName(r) })}</span>
                  {r === member.role ? (
                    <span className="k2">{t('settings.members.current')}</span>
                  ) : null}
                </button>
              ))}
            </>
          ) : null}
          {choices.length > 0 && removable ? <hr /> : null}
          {removable ? (
            <button type="button" className="pal-i" onClick={() => open({ kind: 'remove' })}>
              <Icon name={self ? 'out' : 'x'} />
              <span>{self ? t('settings.members.leave') : t('settings.members.remove')}</span>
            </button>
          ) : null}
        </section>
      ) : null}
      <dialog ref={dialog} aria-labelledby={titleId} onClose={() => setAsk(null)}>
        <div className="sections" style={{ gap: 14 }}>
          <h2 id={titleId} style={{ fontSize: 17 }}>
            {text?.title}
          </h2>
          <p className="se-p">{text?.body}</p>
          <div className="acts">
            <Btn
              // Removing someone is the destructive one: the danger tone, as Delete rule.
              {...(ask?.kind === 'remove'
                ? { className: 'danger' }
                : { variant: 'solid' as const })}
              onClick={() => {
                const next = ask
                dialog.current?.close()
                if (next?.kind === 'role') onRole(next.role)
                else if (next?.kind === 'remove') onRemove()
              }}
            >
              {text?.confirm}
            </Btn>
            <Btn onClick={() => dialog.current?.close()}>{t('settings.cancel')}</Btn>
          </div>
        </div>
      </dialog>
    </>
  )
}

/** Why an action was refused, as a status line (`! …`). */
function Refused({
  error,
  conflict,
  forbidden,
}: {
  error: unknown
  conflict?: string
  forbidden?: string
}) {
  const { t } = useI18n()
  if (!error) return null
  return (
    <p role="alert" className="se-hint">
      <Status
        s="miss"
        label={
          isApiError(error, 409) && conflict
            ? conflict
            : isApiError(error, 403)
              ? (forbidden ?? t('settings.adminOnly'))
              : isApiError(error, 429)
                ? t('settings.rateLimited')
                : isApiError(error, 409)
                  ? t('settings.conflict')
                  : t('state.failed')
        }
      />
    </p>
  )
}

export function MembersPage({
  orgId,
  crumb,
  title,
}: {
  orgId: string
  crumb: string
  title: string
}) {
  const i18n = useI18n()
  const { t, locale } = i18n
  const members = useQuery(membersQuery(orgId))
  const invitations = useQuery(invitationsQuery(orgId))
  const org = useQuery(orgQuery(orgId))
  const me = useQuery(meQuery)
  const invite = useInvite(orgId)
  const withdraw = useWithdrawInvitation(orgId)
  const remove = useRemoveMember(orgId)
  const changeRole = useChangeMemberRole(orgId)
  const navigate = useNavigate()
  const myId = me.data?.user.id
  const role = org.data?.role
  const canInvite = role === 'owner' || role === 'admin'
  const roleName = (r: Role) => t(ROLE_KEY[r])
  const [email, setEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<Role>('member')
  const emailRef = useRef<HTMLInputElement>(null)
  const ids = { email: useId(), role: useId(), invite: useId() }
  function submit(e: FormEvent) {
    e.preventDefault()
    invite.mutate(
      { email: email.trim(), role: inviteRole, locale },
      { onSuccess: () => setEmail('') },
    )
  }
  function removeMember(userId: string) {
    remove.mutate(userId, {
      // Leaving: the organization is gone from this session.
      onSuccess: () => (userId === myId ? navigate({ to: '/' }) : undefined),
    })
  }
  const meta =
    org.data && members.data && invitations.data
      ? t('settings.members.meta', {
          org: org.data.name,
          members: members.data.length,
          invitations: invitations.data.length,
        })
      : undefined
  return (
    <main id="main" className="pg se-pg members">
      <PageHead
        crumb={crumb}
        title={title}
        meta={meta}
        actions={
          canInvite ? (
            <Btn onClick={() => emailRef.current?.focus()}>
              <Icon name="plus" className="se-i" />
              {t('settings.invite.action')}
            </Btn>
          ) : undefined
        }
      />
      <div className="grid2">
        <div className="sections">
          <Section title={t('settings.members')} meta={t('settings.members.sub')}>
            {members.isPending ? (
              <Loading />
            ) : members.isError ? (
              <ErrorState error={members.error} />
            ) : (
              <Table label={t('settings.members')}>
                <thead>
                  <tr>
                    <th className="f">{t('settings.members.name')}</th>
                    <th>{t('settings.members.role')}</th>
                    <th>{t('settings.members.since')}</th>
                    <th className="last">
                      <span className="visually-hidden">{t('settings.keys.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {members.data.map((m) => {
                    const self = m.user_id === myId
                    const choices = roleChoices(m, myId, members.data)
                    const removable = canRemove(m, myId, members.data)
                    return (
                      <tr key={m.user_id}>
                        <td className="f">
                          <span className="se-who">
                            <span className="avatar" aria-hidden="true">
                              {initials(m.name, m.email)}
                            </span>
                            {m.name || m.email}
                          </span>
                          {self ? (
                            <span className="faint"> {t('settings.members.you')}</span>
                          ) : null}
                          <span className="t2 under" translate="no">
                            {m.email}
                          </span>
                        </td>
                        <td>{roleName(m.role).toLowerCase()}</td>
                        <td>{day(i18n, m.created_at)}</td>
                        <td className="last">
                          {choices.length > 0 || removable ? (
                            <MemberMenu
                              member={m}
                              self={self}
                              choices={choices}
                              removable={removable}
                              busy={changeRole.isPending || remove.isPending}
                              onRole={(r) => changeRole.mutate({ userId: m.user_id, role: r })}
                              onRemove={() => removeMember(m.user_id)}
                            />
                          ) : null}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            )}
            <Refused
              error={changeRole.error}
              conflict={t('settings.members.lastOwner')}
              forbidden={t('settings.members.roleNotAllowed')}
            />
            <Refused
              error={remove.error}
              conflict={t('settings.members.lastOwner')}
              forbidden={t('settings.members.notAllowed')}
            />
          </Section>
          <Section title={t('settings.invitations')} meta={t('settings.invitations.sub')}>
            {invitations.isPending ? (
              <Loading />
            ) : invitations.isError ? (
              <ErrorState error={invitations.error} />
            ) : invitations.data.length === 0 ? (
              <EmptyNote title={t('settings.invitations.none')} />
            ) : (
              <Table label={t('settings.invitations')}>
                <thead>
                  <tr>
                    <th className="f">{t('settings.members.email')}</th>
                    <th>{t('settings.members.role')}</th>
                    <th>{t('settings.invitations.sent')}</th>
                    <th>{t('settings.invitations.expires')}</th>
                    <th className="last">
                      <span className="visually-hidden">{t('settings.keys.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.data.map((i) => (
                    <tr key={i.id}>
                      <td className="f">
                        <code translate="no">{i.email}</code>
                      </td>
                      <td>{roleName(i.role).toLowerCase()}</td>
                      <td>{day(i18n, i.created_at)}</td>
                      <td>{day(i18n, i.expires_at)}</td>
                      <td className="last">
                        {canInvite ? (
                          <Btn
                            variant="ghost"
                            size="sm"
                            disabled={withdraw.isPending}
                            onClick={() => withdraw.mutate(i.id)}
                          >
                            {t('settings.invitations.withdraw')}
                          </Btn>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <Refused error={withdraw.error} />
          </Section>
        </div>
        <div className="sections">
          <section className="win" aria-labelledby={ids.invite}>
            <div className="win-bar">
              <b id={ids.invite}>{t('settings.invite.title')}</b>
            </div>
            {canInvite ? (
              <form className="se-form" onSubmit={submit}>
                <label className="lbl" htmlFor={ids.email}>
                  {t('settings.invite.email')}
                  <input
                    ref={emailRef}
                    id={ids.email}
                    className="input"
                    type="email"
                    required
                    placeholder={t('settings.invite.placeholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </label>
                <label className="lbl" htmlFor={ids.role}>
                  {t('settings.members.role')}
                  <select
                    id={ids.role}
                    className="select"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as Role)}
                  >
                    {(['member', 'admin', 'owner'] as const).map((r) => (
                      <option key={r} value={r}>
                        {roleName(r)}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="se-row">
                  <Btn type="submit" variant="solid" disabled={invite.isPending}>
                    {t('settings.invite.send')}
                  </Btn>
                </div>
                {invite.isSuccess ? (
                  <p role="status" className="se-hint">
                    <Status s="ok" label={t('settings.invite.sent')} />
                  </p>
                ) : null}
                <Refused error={invite.error} />
              </form>
            ) : role ? (
              <p className="se-form se-hint">{t('settings.invite.adminOnly')}</p>
            ) : null}
          </section>
        </div>
      </div>
    </main>
  )
}
